import { maskValue } from './mask'
import formatDate from '@niaratech/niara-js-commons/dist/formatDate'
import printPercentage from '@niaratech/niara-js-commons/dist/printPercentage'
import defaultLang from '@niaratech/niara-js-commons/dist/default-lang'
import cloneDeep from 'lodash/cloneDeep'
import t from '@niaratech/niara-js-commons/dist/t'
import type {
  Type,
  CrudMetadata,
  Field,
  PartialField,
  GraphQLOperation,
  JsonSchemaProperty,
  JsonSchema,
  PartialCrudMetadata,
} from './index'
export type {
  Type,
  CrudMetadata,
  Field,
  PartialField,
  GraphQLOperation,
  JsonSchemaProperty,
  CrudModel,
  JsonSchema,
  Filter,
  PartialCrudMetadata,
} from './types'

const _numberFormatCache: Record<string, Intl.NumberFormat> = {}
const _formatNumber = (value: number, locale: string = defaultLang()): string => {
  const numberFormat = _numberFormatCache[locale]
    ? _numberFormatCache[locale]
    : (_numberFormatCache[locale] = new Intl.NumberFormat(locale, {}))
  return numberFormat.format(value)
}

export const graphQLTypeFromType = (type: Type): string => {
  return type === 'int'
    ? 'Int'
    : type === 'float'
    ? 'Float'
    : type === 'boolean'
    ? 'Boolean'
    : type === 'object'
    ? 'AWSJSON'
    : type === 'textarray'
    ? '[String]'
    : type === 'textarrayarray'
    ? '[[String]]'
    : type === 'intarray'
    ? '[Int]'
    : type === 'floatarray'
    ? '[Float]'
    : type === 'epochseconds'
    ? 'Int'
    : 'String'
}

const _arrayOfType = (type: Type): 'textarray' | 'floatarray' | 'intarray' | 'objectarray' | 'textarrayarray' => {
  switch (type) {
    case 'text':
      return 'textarray'
    case 'textarray':
      return 'textarrayarray'
    case 'float':
      return 'floatarray'
    case 'int':
      return 'intarray'
    case 'object':
      return 'objectarray'
  }
  throw new Error("can't find an array type for " + type)
}

const _createGraphQLType = (
  field: Field | PartialField | Partial<Field>,
  isInput: boolean,
  graphQLTypeSuggestion: string
): string => {
  if (field.fields && graphQLTypeSuggestion && (field.type == 'object' || field.type == 'objectarray')) {
    // creates a string to identity the type
    const typeName = graphQLTypeSuggestion + (isInput ? 'Input' : '')
    if (field.type == 'objectarray') {
      return '[' + typeName + ']'
    }
    return typeName
  }
  return graphQLTypeFromType(field.type)
}

const RETURN_FALSE = function () {
  return false
}
export class Renderers implements Record<Type, (v) => string> {
  datetime = (v) => (!v ? '' : formatDate(v))
  date = (v) => (!v ? '' : formatDate(v, undefined, 'P'))
  boolean = (v) => (v === true ? 'Sim' : v === false ? 'Não' : '')
  object = (v) => (!v ? '' : JSON.stringify(v))
  objectarray = (v) => (!v ? '' : v.map((i) => this.object(i)).join(', '))
  text = (v) => v
  textarray = (v: string[]) => (!v ? '' : v.map((i) => this.text(i)).join(', '))
  textarrayarray = (v: string[][]) => (!v ? '' : v.map((i) => this.textarray(i)).join(', '))
  int = (v) => (v == null ? '' : _formatNumber(v))
  epochseconds = (v) => (v == null ? '' : _formatNumber(v))
  intarray = (v) => (!v ? '' : v.map((i) => this.int(i)).join(', '))
  floatarray = (v) => (!v ? '' : v.map((i) => _formatNumber(i)).join(', '))
  float = (v) => (v == null ? '' : _formatNumber(v))
  percentage = (v) => (v == null ? '' : printPercentage(v))
  _option = (v, options) => [...options.filter((o) => o.value === v).map((o) => o.label), v][0]
  option = (v, options) =>
    v == null ? '' : v.map ? v.map((i) => this._option(i, options)).join(', ') : this._option(v, options)
  mask = (v, mask) => (v == null ? '' : maskValue(v, mask))
}
const renderers = new Renderers()

export function fillFieldGaps<E extends Record<string, any> = Record<string, any>>(
  param: Partial<Field<any, E>> | PartialField<any, E>,
  idx?: number,
  graphQLTypeSuggestion?: string
): Field {
  const field = param as Partial<Field<any, E>>
  field.type = field.type || 'text'
  if (field.options?.length > 0) {
    field.options = field.options.map((option) =>
      (field.type === 'text' || field.type === 'textarray' || field.type === 'textarrayarray') &&
      typeof option === 'string'
        ? { value: option, label: option }
        : (field.type === 'int' || field.type === 'float') && typeof option === 'number'
        ? { value: option, label: option }
        : option
    )
  }

  field.updatable = field.updatable ? true : field.updatable !== false && !field.id
  field.required = field.required === true
  field.insertable = field.insertable !== false
  field.label = field.label || field.name[0].toUpperCase() + field.name.substring(1).replace(/[A-Z]/g, ' $&')
  field.labelPlural = field.labelPlural || field.label + 's'
  field.graphQLInputType =
    field.graphQLInputType ?? field.graphQLType ?? _createGraphQLType(field, true, graphQLTypeSuggestion)
  field.graphQLType = field.graphQLType ? field.graphQLType : _createGraphQLType(field, false, graphQLTypeSuggestion)
  field.fetchByDefault = field.fetchByDefault ?? (field.deprecated ? false : true)

  const format: Field['format'] =
    field.format != null
      ? field.format
      : field.options?.length > 0
      ? (v) => {
          return renderers.option(v, field.options)
        }
      : field.inputType == 'percentage'
      ? renderers.percentage
      : field.mask
      ? (v) => renderers.mask(v, field.mask)
      : renderers[field.type] ?? ((i) => i)
  field.format = format

  const isRenderFalse = param.render === false

  field.render = isRenderFalse
    ? RETURN_FALSE
    : field.render !== undefined
    ? field.render
    : function (item) {
        return this.format(item[field.name])
      }

  if (field.renderStyle === undefined) {
    if (field.name === 'enabled' && field.type === 'boolean') {
      field.renderStyle = (i, fieldName = 'enabled') => (i[fieldName] ? 'success' : 'danger')
    } else {
      field.renderStyle = () => null
    }
  }

  if (field.renderForm === undefined) {
    field.renderForm = isRenderFalse ? false : field.render
  }
  if (field.renderColumn === undefined) {
    field.renderColumn = isRenderFalse ? false : field.render
  }

  if (field.columnClass === undefined && field.renderStyle) {
    field.columnClass = function (i) {
      const style = this.renderStyle && this.renderStyle?.(i, field.name)
      return style ? 'text-' + style : undefined
    }
  }

  // bind methods to field
  const METHODS_TO_BIND = [
    'render',
    'renderForm',
    'renderColumn',
    'renderStyle',
    'conditionalRequired',
    'columnClass',
    'onEntityChange',
    'format',
    'initialize',
  ] as const

  field['renderers'] = renderers
  METHODS_TO_BIND.filter((p) => field[p] && typeof field[p] === 'function').forEach(
    (p) => (field[p] = (field[p] as { (any): any }).bind(field))
  )

  if (field.fields) {
    field.fields.forEach((f, idx) =>
      fillFieldGaps(f, idx, graphQLTypeSuggestion ? graphQLTypeSuggestion + '_' + f.name : undefined)
    )
    field.fieldMap = field.fields.reduce((ac, f) => {
      {
        ac[f.name] = f
        return ac
      }
    }, {})
  }
  field.default =
    field.default !== undefined
      ? field.default
      : field.type === 'datetime'
      ? null
      : field.type === 'date'
      ? null
      : field.type === 'float'
      ? null
      : field.type === 'int'
      ? null
      : field.type === 'boolean'
      ? field.required
        ? false
        : null
      : field.type === 'object'
      ? null
      : field.type === 'textarray'
      ? []
      : field.type === 'textarrayarray'
      ? []
      : field.type === 'intarray'
      ? []
      : field.type === 'floatarray'
      ? []
      : field.type === 'objectarray'
      ? []
      : ''

  return field as Field
}

export function fillFieldsGaps<E extends Record<string, any> = Record<string, any>>(
  fields: Array<Field<any, E> | Partial<Field<any, E>>>,
  graphQLTypePrefix?: string
): Array<Field<any, E>> {
  return fields?.map((f, idx) =>
    fillFieldGaps(f, idx, graphQLTypePrefix != null ? graphQLTypePrefix + '_' + f.name : undefined)
  )
}

function _jsonSchemaProperties(target: JsonSchemaProperty, fields: Array<Field>) {
  const { properties, required } = target
  fields.forEach((field) => {
    const propObject: JsonSchemaProperty = {
      description: field.label,
      type:
        field.type === 'datetime'
          ? 'string'
          : field.type === 'date'
          ? 'string'
          : field.type === 'float'
          ? 'number'
          : field.type === 'int'
          ? 'integer'
          : field.type === 'boolean'
          ? 'boolean'
          : field.type === 'object'
          ? 'object'
          : field.type === 'objectarray'
          ? 'array'
          : field.type === 'textarray'
          ? 'array'
          : field.type === 'textarrayarray'
          ? 'array'
          : field.type === 'intarray'
          ? 'array'
          : field.type === 'floatarray'
          ? 'array'
          : field.type === 'text'
          ? 'string'
          : 'object',
    }
    if (field.type === 'textarray') {
      propObject.items = {
        type: 'string',
      }
    }
    if (field.type === 'textarrayarray') {
      propObject.items = {
        type: 'array',
        items: {
          type: 'string',
        },
      }
    }
    if (field.type === 'intarray') {
      propObject.items = {
        type: 'integer',
      }
    }
    if (field.type === 'floatarray') {
      propObject.items = {
        type: 'number',
      }
    }
    if (field.type === 'objectarray') {
      propObject.items = {
        type: 'object',
        properties: {},
        required: [],
      }
      _jsonSchemaProperties(propObject.items, field.fields)
    }
    if (field.type === 'text') {
      if (field.mask) {
        propObject.maxLength = field.mask.match(/\w/g).length
      }
      if (field.required) {
        propObject.minLength = 1
        if (field.mask) {
          propObject.minLength = field.mask.match(/\w/g).length
        }
      }
      if (field.minLength != null) propObject.minLength = field.minLength
      if (field.maxLength != null) propObject.maxLength = field.maxLength
      if (field.pattern) propObject.pattern = field.pattern
    }

    if (field.options?.length > 0) {
      switch (field.type) {
        case 'textarray':
        case 'floatarray':
        case 'intarray': {
          propObject.items.enum = field.options.map((r) => r.value)
          break
        }
        default: {
          propObject.enum = field.options.map((r) => r.value)
          break
        }
      }
    }
    properties[field.name] = propObject
    if (field.required) required.push(field.name)
  })
}

export function createJsonSchema(name, title, fields): JsonSchema {
  const schema: JsonSchema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    $id: `http://devcase.com.br/${name}.schema.json`,
    title: title,
    type: 'object',
    properties: {},
    required: [],
  }

  _jsonSchemaProperties(schema, fields)

  return schema
}

const filterI18nFields = (fields: Field[]): Field[] => {
  if (!fields) return fields
  return fields
    .map((field) => {
      if (field.type == 'object' || field.type == 'objectarray') {
        const subfields = filterI18nFields(field.fields)
        if (subfields?.length > 0) {
          return { ...field, fields: subfields }
        } else {
          return null
        }
      }
      if (field.i18n_enabled) {
        return field
      } else {
        return null
      }
    })
    .filter((r) => r)
}

export default function <E = any>(crudMetadata_: PartialCrudMetadata<E>): CrudMetadata<E> {
  const crudMetadata = crudMetadata_ as Partial<CrudMetadata<E>>
  const entityName = crudMetadata.name
  crudMetadata.key = crudMetadata.key || crudMetadata.name[0].toLowerCase() + crudMetadata.name.substring(1)

  crudMetadata.namePlural = crudMetadata.namePlural || crudMetadata.name + 's'
  crudMetadata.path =
    crudMetadata.path || '/' + crudMetadata.namePlural[0].toLowerCase() + crudMetadata.namePlural.substring(1)

  if (crudMetadata?.i18n_enabled && Array.isArray(crudMetadata?.fields)) {
    const translatableFields = filterI18nFields(crudMetadata?.fields)
    crudMetadata.fields = crudMetadata.fields.concat([
      {
        name: '_i18nTranslation',
        type: 'objectarray',
        renderForm: false,
        fields: [
          {
            name: 'locale',
          },
          ...translatableFields,
        ],
      },
    ] as unknown as Field<any, E>[])
  } else {
    crudMetadata.fields || []
  }

  fillFieldsGaps<E>(crudMetadata.fields, crudMetadata.name)

  crudMetadata.createParams = crudMetadata.createParams || []
  fillFieldsGaps<E>(crudMetadata.createParams, crudMetadata.name + '_CreateParam')

  crudMetadata.updateParams = crudMetadata.updateParams || []
  fillFieldsGaps<E>(crudMetadata.updateParams, crudMetadata.name + '_UpdateParam')

  crudMetadata.searchParams = crudMetadata.searchParams || []
  fillFieldsGaps<E>(crudMetadata.searchParams, crudMetadata.name + '_SearchParam')

  crudMetadata.getParams = crudMetadata.getParams || []
  fillFieldsGaps<E>(crudMetadata.getParams, crudMetadata.name + '_GetParam')

  if (crudMetadata.updatable === undefined) {
    crudMetadata.updatable = crudMetadata.fields.filter((f) => f.updatable).length > 0
  }

  crudMetadata.insertable = crudMetadata.insertable !== false
  crudMetadata.deletable = crudMetadata.deletable !== false
  crudMetadata.searchable = crudMetadata.searchable !== false

  //var idfield = crudMetadata.fields.filter((f) => f.name == "id")[0];
  const idfields = crudMetadata.fields.filter((f) => f.id) as Field<E>[]
  crudMetadata.idFields = idfields

  crudMetadata.findFieldByName = function (name) {
    return this.fields.filter((f) => f.name === name)[0]
  }.bind(crudMetadata)

  crudMetadata.fieldMap = crudMetadata.fields.reduce((ac, f) => {
    {
      ac[f.name] = f
      return ac
    }
  }, {} as Record<Extract<keyof E, string>, any>)

  crudMetadata.getId =
    crudMetadata.getId || idfields.length == 1
      ? (item) => item[idfields[0].name]
      : (item) => idfields.map((f) => item[f.name]).join(';')
  crudMetadata.getIdMap = function (item) {
    return idfields.reduce((acc, f) => {
      acc[f.name] = item[f.name]
      return acc
    }, {})
  }

  if (crudMetadata_.columnNames && !crudMetadata.columns) {
    crudMetadata.columns = []
    crudMetadata_.columnNames.forEach((columnName) => {
      const c = crudMetadata.fields.filter((f) => f.name === columnName)[0]
      if (c) crudMetadata.columns.push(c)
    })
  }

  crudMetadata.columns = crudMetadata.columns || (crudMetadata.fields as Field[])
  crudMetadata.columns = crudMetadata_.columns
    .map((field, idx) => {
      if (typeof field === 'string') {
        field = crudMetadata.fields.filter((f) => f.name === field)[0]
      }
      return fillFieldGaps(field, idx, crudMetadata.name + '_' + field.name)
    })
    .filter((x) => x)

  if (crudMetadata_.importColumnNames) {
    crudMetadata.importColumns = []
    crudMetadata_.importColumnNames.forEach((columnName) => {
      const c = crudMetadata.fields.filter((f) => f.name == columnName)[0]
      if (c) crudMetadata.importColumns.push(c)
    })
  }

  //JSON Schema, para validação
  crudMetadata.jsonSchema =
    crudMetadata.jsonSchema || createJsonSchema(crudMetadata.name, crudMetadata.label, crudMetadata.fields)

  function addFieldToQuery(f: Field) {
    if (f.fields && f.fields.length > 0) {
      return t`
        ${f.name} {
          ${f.fields.map(addFieldToQuery).join('\n')}
        }`
    } else {
      return f.name
    }
  }
  const queryColumns = crudMetadata.columns.map(addFieldToQuery).join('\n')
  const queryFields = crudMetadata.fields
    .filter((r) => !r.deprecated)
    .map(addFieldToQuery)
    .join('\n')

  const filter = crudMetadata.filter || []
  filter.forEach((c) => {
    if ('field' in c && c.field && 'op' in c) {
      const field = 'field' in c ? crudMetadata.fields.filter((f) => f.name === c.field)[0] : undefined
      if (c.graphQLType === undefined) {
        c.graphQLType =
          c.op === 'isNull' ? 'Boolean' : c.op === 'in' ? '[' + field.graphQLType + ']' : field.graphQLType
      }
      if (c.type === undefined) {
        c.type = c.op === 'isNull' ? 'boolean' : c.op === 'in' ? _arrayOfType(field.type) : field.type
      }
      if (c.name === undefined) {
        c.name = c.field + '_' + c.op
      }
      c.label = c.label || c.field[0].toUpperCase() + c.field.substring(1).replace(/[A-Z]/g, ' $&')
    }
  })

  crudMetadata.graphQL = crudMetadata.graphQL || {}

  if (crudMetadata.searchable && !crudMetadata.graphQL.search) {
    const operation: Partial<GraphQLOperation> = crudMetadata.graphQL.search || {}

    const queryArguments: Array<string> = ['nextToken: $nextToken', 'limit: $limit']
    const queryVariables: Array<string> = ['$nextToken: String', '$limit: Int']
    if (filter.length > 0) {
      const _inputSearchParams = []
      filter.forEach((f) => {
        queryVariables.push(`$${f.name}: ${f.graphQLType}`)
      })
      //filtros que não usam StringFieldConditions
      filter
        .filter((f) => !('field' in f))
        .forEach((f) => {
          _inputSearchParams.push(`${f.name}: $${f.name}`)
        })
      //filtros com fieldConditions
      const filterFields = filter
        .filter((c) => 'field' in c && c.field)
        .map((c) => 'field' in c && c.field)
        .filter((value, index, self) => self.indexOf(value) === index)
      filterFields.forEach((name) => {
        _inputSearchParams.push(
          `${name}: {${filter
            .filter((c) => 'field' in c && c.field === name)
            .map((c) => ('op' in c ? `${c.op}: $${c.name}` : undefined))
            .join(', ')}}`
        )
      })
      queryArguments.push(`input: {${_inputSearchParams.join(', ')}}`)
    }
    if (crudMetadata.searchParams) {
      crudMetadata.searchParams.forEach((f) => {
        queryVariables.push(`$${f.name}: ${f.graphQLInputType}`)
        queryArguments.push(`${f.name}: $${f.name}`)
      })
    }

    const queryArgumentsStr = queryArguments.join(', ')
    const queryVariablesStr = queryVariables.join(', ')

    operation.name = operation.name || 'search' + entityName
    operation.query =
      operation.query ||
      `query Search(${queryVariablesStr}) {
            ${operation.name}(${queryArgumentsStr}) {
                items {
                    ${queryColumns}
                }
                nextToken
            }
        }
        `
    Object.assign(operation, {
      queryVariables: queryVariablesStr,
      queryArguments: queryArgumentsStr,
    })
    crudMetadata.graphQL.search = operation as GraphQLOperation
  }

  if (!crudMetadata.graphQL.findById) {
    const operation: Partial<GraphQLOperation> = crudMetadata.graphQL.findById || {}

    operation.name = operation.name || entityName[0].toLowerCase() + entityName.substring(1)
    const queryVariables = [...idfields, ...crudMetadata.getParams]
      .map((f) => `$${f.name}: ${f.graphQLType}${f.required ? '!' : ''}`)
      .join(', ')
    const queryArguments = [...idfields, ...crudMetadata.getParams].map((f) => `${f.name}: $${f.name}`).join(', ')
    operation.query =
      operation.query ||
      t`
        query FindById(${queryVariables}) {
          ${operation.name}(${queryArguments}) {
            ${queryFields}
          }
        }
      `
    Object.assign(operation, { queryVariables, queryArguments })
    crudMetadata.graphQL.findById = operation as GraphQLOperation
  }

  if (crudMetadata.updatable && !crudMetadata.graphQL.update) {
    const operation: Partial<GraphQLOperation> = crudMetadata.graphQL.update || {}

    operation.name = operation.name || 'update' + entityName
    operation.queryVariables = [
      ...[...crudMetadata.fields.filter((f) => f.updatable !== false), ...crudMetadata.updateParams].map(
        (f) => `$${f.name}: ${f.graphQLInputType}`
      ),
      ...idfields.map((f) => `$${f.name}: ${f.graphQLInputType}${f.required ? '!' : ''}`),
    ].join(', ')
    operation.queryArguments = `input: {${crudMetadata.fields
      .filter((f) => f.updatable !== false)
      .map((f) => f.name + ': ' + '$' + f.name)
      .join(', ')}}, ${[...idfields, ...crudMetadata.updateParams]
      .map((f) => f.name)
      .map((f) => `${f}: $${f}`)
      .join(', ')}`

    operation.mutation =
      operation.mutation ||
      t`
        mutation Update(${operation.queryVariables}) {
          ${operation.name}(${operation.queryArguments}) {
            ${queryFields}    
          } 
        }
      `
    crudMetadata.graphQL.update = operation as GraphQLOperation
  }

  if (crudMetadata.insertable) {
    const operation: Partial<GraphQLOperation> = crudMetadata.graphQL.create || {}
    operation.name = operation.name || 'create' + entityName
    operation.queryVariables = [
      ...idfields.filter((f) => f.insertable).map((f) => `$${f.name}: ${f.graphQLInputType}${f.required ? '!' : ''}`),
      ...crudMetadata.fields
        .filter((f) => f.insertable !== false)
        .filter((f) => !f.id)
        .map((f) => '$' + f.name + ': ' + f.graphQLInputType + (f.required ? '!' : '')),
      ...crudMetadata.createParams.map((p) => `$${p.name}: ${p.graphQLInputType}${p.required ? '!' : ''}`),
    ].join(', ')
    operation.queryArguments = `input: { 
            ${crudMetadata.fields
              .filter((f) => f.insertable !== false)
              .map((f) => f.name + ': ' + '$' + f.name)
              .join(', ')} 
        }${
          crudMetadata.createParams.length == 0
            ? ''
            : ', ' + crudMetadata.createParams.map((p) => `${p.name}: $${p.name}`).join(', ')
        }
        `
    operation.mutation =
      operation.mutation ||
      t`
        mutation Create(${operation.queryVariables}) {
          ${operation.name}(${operation.queryArguments}) {
            ${queryFields}
          }
        }
      `
    crudMetadata.graphQL.create = operation as GraphQLOperation
  }

  if (crudMetadata.deletable && !crudMetadata.graphQL.delete) {
    const operation: Partial<GraphQLOperation> = crudMetadata.graphQL.delete || {}

    operation.name = operation.name || 'delete' + entityName
    operation.queryVariables = idfields.map((f) => `$${f.name}: ${f.graphQLType}!`).join(', ')
    operation.queryArguments = idfields.map((f) => `${f.name}: $${f.name}`).join(', ')

    operation.mutation =
      operation.mutation ||
      t`
        mutation Delete(${operation.queryVariables}) {
          ${operation.name}(${operation.queryArguments}) {
            ${queryFields}
          }
        }
      `
    crudMetadata.graphQL.delete = operation as GraphQLOperation
  }

  crudMetadata.hasId =
    crudMetadata.hasId ||
    function (obj) {
      return !(idfields.filter((f) => !obj[f.name]).length > 0)
    }

  crudMetadata.dynamoDB = crudMetadata.dynamoDB || {}
  crudMetadata.dynamoDB.tableName = crudMetadata.dynamoDB.tableName || 'Spear' + crudMetadata.name
  crudMetadata.dynamoDB.partitionKey =
    crudMetadata.dynamoDB.partitionKey !== undefined ? crudMetadata.dynamoDB.partitionKey : 'tenantId'
  crudMetadata.dynamoDB.sortKey = crudMetadata.dynamoDB.sortKey !== undefined ? crudMetadata.dynamoDB.sortKey : 'id'

  crudMetadata.newFromMetadata = () => newFromMetadata<E>(crudMetadata as CrudMetadata<E>)
  return crudMetadata as CrudMetadata<E>
}

export const newFromMetadata = function <E = any>(crudMetadata: Pick<CrudMetadata<E>, 'fields' | 'createParams'>): E {
  const instance: Partial<E> = {}
  crudMetadata.fields.concat(crudMetadata.createParams || []).forEach((field) => {
    if (field.default !== undefined) {
      if (field.default != null && typeof field.default == 'object') {
        instance[field.name] = cloneDeep(field.default)
      } else {
        instance[field.name] = field.default
      }
    }
    if (field.initialize && typeof field.initialize === 'function') {
      instance[field.name] = field.initialize()
    }
  })
  return instance as E
}
